import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { calculatePeriodStats } from '../utils/energyLogic';
import { COLORS } from '../utils/constants';

const { FiX, FiCalendar, FiTrendingUp, FiZap } = FiIcons;

const AnalysisOverlay = ({ readings, meter, onClose }) => {
  const [older, newer] = readings.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const stats = calculatePeriodStats(older, newer, meter);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>
            Zeitraum-Analyse
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Error Message */}
          {stats.error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              <strong>⚠️ Hinweis:</strong> {stats.error}
              {meter.type === 'gas' && stats.error.includes('Gas-Parameter') && (
                <p className="text-sm mt-2">
                  Bitte tragen Sie Zustandszahl und Brennwert beim Erfassen der Ablesung ein. 
                  Diese Werte finden Sie auf Ihrer Gasrechnung.
                </p>
              )}
            </div>
          )}

          {/* Selected Readings */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-600 font-semibold mb-2">Von (älter)</div>
              <div className="text-lg font-bold text-blue-900">
                {format(new Date(older.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
              </div>
              <div className="text-sm text-blue-700 mt-1">
                Stand: {older.value.toFixed(2)} {meter.type === 'gas' || meter.type === 'water' ? 'm³' : 'kWh'}
              </div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-sm text-green-600 font-semibold mb-2">Bis (neuer)</div>
              <div className="text-lg font-bold text-green-900">
                {format(new Date(newer.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
              </div>
              <div className="text-sm text-green-700 mt-1">
                Stand: {newer.value.toFixed(2)} {meter.type === 'gas' || meter.type === 'water' ? 'm³' : 'kWh'}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {!stats.error && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-6 bg-white rounded-xl shadow-lg border-2" style={{ borderColor: COLORS.primary }}>
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={FiCalendar} size={24} style={{ color: COLORS.primary }} />
                  <div className="text-sm text-gray-600">Zeitraum</div>
                </div>
                <div className="text-3xl font-bold" style={{ color: COLORS.primary }}>
                  {stats.days}
                </div>
                <div className="text-sm text-gray-500">Tage</div>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-lg border-2" style={{ borderColor: COLORS.success }}>
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={FiZap} size={24} style={{ color: COLORS.success }} />
                  <div className="text-sm text-gray-600">Gesamtverbrauch</div>
                </div>
                <div className="text-3xl font-bold" style={{ color: COLORS.success }}>
                  {stats.consumption}
                </div>
                <div className="text-sm text-gray-500">kWh</div>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-lg border-2" style={{ borderColor: COLORS.secondary }}>
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={FiTrendingUp} size={24} style={{ color: COLORS.secondary }} />
                  <div className="text-sm text-gray-600">Durchschnitt pro Tag</div>
                </div>
                <div className="text-3xl font-bold" style={{ color: COLORS.secondary }}>
                  {stats.perDay}
                </div>
                <div className="text-sm text-gray-500">kWh/Tag</div>
              </div>

              <div className="p-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl shadow-lg text-white">
                <div className="flex items-center gap-3 mb-2">
                  <SafeIcon icon={FiTrendingUp} size={24} />
                  <div className="text-sm opacity-90">365-Tage-Prognose</div>
                </div>
                <div className="text-3xl font-bold">
                  {stats.yearlyForecast}
                </div>
                <div className="text-sm opacity-90">kWh/Jahr</div>
              </div>
            </div>
          )}

          {/* Gas Info */}
          {meter.type === 'gas' && newer.gasParams && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: `${COLORS.gas}15`, borderColor: COLORS.gas, borderWidth: 2 }}>
              <h4 className="font-bold mb-2" style={{ color: COLORS.gas }}>
                Gas-Umrechnung verwendet:
              </h4>
              <div className="text-sm space-y-1">
                <div>Zustandszahl: <strong>{newer.gasParams.zNumber.toFixed(4)}</strong></div>
                <div>Brennwert: <strong>{newer.gasParams.heatingValue.toFixed(3)} kWh/m³</strong></div>
                <div className="text-xs text-gray-600 mt-2">
                  Formel: kWh = (m³ neu - m³ alt) × Zustandszahl × Brennwert
                </div>
              </div>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-3 px-6 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
            style={{ backgroundColor: COLORS.primary }}
          >
            Schließen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AnalysisOverlay;